import React, { useState, useEffect, useCallback } from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { TILE_SIZE, TILE_SIZE_RESPONSIVE } from "./constants";
import { TileDialog } from "./tileDialog/TileDialog";
import { Tile } from "./Tile";
import ConfiguratorLayouts from "./generalOptions/ConfiguratorLayouts";
import ConfiguratorModalManager from "./ConfiguratorModalManager";
import ConfirmationDialog from "../dialog/ConfirmationDialog";
import ConfigTopOptions from "./generalOptions/ConfigTopOptions";
import { useDispatch } from "react-redux";
import {
  updateElement,
  updateGridKeysToDisplay,
  updateGridSize,
  updateModalManager,
  updateMultiUpload,
} from "../../reducers/configurator/configuratorSlice";
import { resetLayers } from "../../reducers/configurator/utils";
import { Add, Remove, PlayCircle, PlayArrow } from "@mui/icons-material";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from "@mui/material";
import { useViewport } from "../../helpers";
import ObjectsOverview from "./ObjectsOverview";
import { useTranslation } from "react-i18next";
import ThreeDimensionsConfigurator from "./modelManager/ThreeDimensionsConfigurator";
import BasketCourt from "../../assets/basketCourt.png";
import FootballCourt from "../../assets/footballCourt.png";
import PickleballCourt from "../../assets/pickleballCourt.png";
import { gamesSizesHelper, gamesTypes } from "./utils/utils";

const PREFIX = "Configurator";

const classes = {
  gridSizing: `${PREFIX}-gridSizing`,
  gridMargin: `${PREFIX}-gridMargin`,
  root: `${PREFIX}-root`,
  rowStyle: `${PREFIX}-rowStyle`,
  groundLogoContainer: `${PREFIX}-groundLogoContainer`,
  imgStyle: `${PREFIX}-imgStyle`,
  tileStyleDefault: `${PREFIX}-tileStyleDefault`,
  tileStyleFirstRow: `${PREFIX}-tileStyleFirstRow`,
  tileStyleAllRows: `${PREFIX}-tileStyleAllRows`,
  tileStyleFirstColumn: `${PREFIX}-tileStyleFirstColumn`,
  tileCenterColor: `${PREFIX}-tileCenterColor`,
  themeRadiusWrapper: `${PREFIX}-themeRadiusWrapper`,
  themeRadius: `${PREFIX}-themeRadius`,
  skeletonRoot: `${PREFIX}-skeletonRoot`,
  sizingContainer: `${PREFIX}-sizingContainer`,
  gridSizingBox: `${PREFIX}-gridSizingBox`,
  marginSeparator: `${PREFIX}-marginSeparator`,
  launchButton: `${PREFIX}-launchButton`,
};

const ConfiguratorRoot = styled("div")(({ theme }) => {
  return {
    [`& .${classes.gridSizing}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      maxWidth: "100%",
      overflow: "auto",
      // marginBottom: "350px",
      marginBottom: "5rem",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        borderRadius: "10px",
      },
      padding: "0 1rem 1rem 1rem",
    },
    [`& .${classes.gridSizingBox}`]: {
      display: "flex",
      alignItems: "center",
      border: "1px solid #D3AF5F",
      borderRadius: "5px 5px 5px 5px",
      width: "90px",
    },
    [`& .${classes.marginSeparator}`]: {
      margin: "0 10px 0 10px",
    },
    [`& .${classes.root}`]: {},
    [`& .${classes.sizingContainer}`]: {
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      marginBottom: "1rem",
    },
    [`& .${classes.rowStyle}`]: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
    },
    [`& .${classes.gridMargin}`]: {
      marginBottom: "412px",
    },
    [`& .${classes.groundLogoContainer}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        height: `${TILE_SIZE_RESPONSIVE}px`,
        width: `${TILE_SIZE_RESPONSIVE}px`,
      },
      height: `${TILE_SIZE}px`,
      width: `${TILE_SIZE}px`,
      zIndex: 90,
      position: "absolute",
      top: "0",
    },
    [`& .${classes.imgStyle}`]: {
      position: "absolute",
      [theme.breakpoints.between("xs", "sm")]: {
        height: `${TILE_SIZE_RESPONSIVE}px`,
        width: `${TILE_SIZE_RESPONSIVE}px`,
      },
      height: `${TILE_SIZE}px`,
      width: `${TILE_SIZE}px`,
      objectFit: "contain",
      zIndex: "2",
    },
    [`& .${classes.themeRadiusWrapper}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        height: `${TILE_SIZE_RESPONSIVE}px`,
        width: `${TILE_SIZE_RESPONSIVE}px`,
      },
      height: `${TILE_SIZE}px`,
      width: `${TILE_SIZE}px`,
      zIndex: "50",
      position: "absolute",
      top: "0",
      left: "0",
    },
    [`& .${classes.themeRadius}`]: {
      border: "1px solid rgba(211, 175, 95, 1)",
      backgroundColor: "rgba(211, 175, 95, 0.4)",
      [theme.breakpoints.between("xs", "sm")]: {
        height: `${TILE_SIZE_RESPONSIVE}px`,
        width: `${TILE_SIZE_RESPONSIVE}px`,
      },
      height: `${TILE_SIZE}px`,
      width: `${TILE_SIZE}px`,
    },
    [`& .${classes.skeletonRoot}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        height: `${TILE_SIZE_RESPONSIVE}px`,
        width: `${TILE_SIZE_RESPONSIVE}px`,
      },
      height: `${TILE_SIZE}px`,
      width: `${TILE_SIZE}px`,
    },
    [`& .${classes.launchButton}`]: {
      [theme.breakpoints.between("xs", "sm")]: {
        marginLeft: "1.5rem",
        fontSize: "2rem",
      },
      position: "absolute",
      right: "1rem",
    },
  };
});

export default function Configurator(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { width } = useViewport();

  const [isIsoMode, setIsIsoMode] = useState(false);

  const grid = useSelector((state) => state.configurator?.grid);
  const currentTile = useSelector((state) => state.configurator?.selectedTile);
  const confirmation = useSelector((state) => state.configurator.confirmation);
  const multiUpload = useSelector((state) => state.configurator.multiUpload);
  const rowNumber = useSelector((state) => state.configurator.rowNumber);
  const columnNumber = useSelector((state) => state.configurator.columnNumber);
  const isMultiactive = useSelector(
    (state) => state.configurator?.multiSelection.active
  );
  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const parentedLayoutsList = useSelector(
    (state) => state.configurator.parentedLayoutsList
  );
  const layout = useSelector((state) => state.configurator.layout);
  const is3dView = useSelector((state) => state.configurator.is3dView);
  const isBackdropOn = useSelector((state) => state.configurator.backdrop);
  const gridsKeysToDisplay = useSelector(
    (state) => state.configurator.gridsKeysToDisplay
  );

  useEffect(() => {
    if (
      multiUpload.active &&
      multiUpload.orientation &&
      multiUpload.currentTile
    ) {
      dispatch(
        updateElement({
          layer: multiUpload.orientation,
          data: {
            ...multiUpload.currentTile[multiUpload.orientation],
            file: multiUpload.activeFile,
            type: "frame",
            rotation: multiUpload.orientation,
            size: multiUpload.size || "medium",
            visibleRoom: true,
            molding: "design",
          },
          tile: {
            row: multiUpload.currentTile.row,
            column: multiUpload.currentTile.column,
          },
        })
      );
      const filteredFiles = multiUpload.files.filter(
        (file) => file.name !== multiUpload.activeFile.name
      );
      dispatch(
        updateMultiUpload({
          files: filteredFiles,
          activeFile: null,
          active: filteredFiles.length === 0 ? false : true,
          orientation: null,
          currentTile: null,
        })
      );
      dispatch(updateModalManager(resetLayers.modalManager));
    }
  }, [multiUpload.orientation]);

  const getGridsFromLayoutList = useCallback(() => {
    const GridsToDisplay = [parentLayout];
    Object.keys(parentedLayoutsList).forEach((key) => {
      if (parentedLayoutsList[key].includes(parentLayout))
        GridsToDisplay.push(key);
    });
    return GridsToDisplay;
  }, [parentLayout, parentedLayoutsList]);

  useEffect(() => {
    dispatch(updateGridKeysToDisplay(getGridsFromLayoutList()));
  }, [parentLayout, dispatch, getGridsFromLayoutList]);

  const isChildAttachedToLayout = () => {
    const currentGrids = getGridsFromLayoutList();
    let parent = null;
    Object.keys(parentedLayoutsList).forEach((key) => {
      if (parentedLayoutsList[key].includes(parentLayout)) parent = key;
    });
    return currentGrids.length > 1 && parent ? true : false;
  };

  const isReduceLocked = (alignment) => {
    return (
      isChildAttachedToLayout() ||
      (gamesTypes.includes(parentLayout) &&
        ((alignment === "row" &&
          columnNumber === gamesSizesHelper[parentLayout].column) ||
          (alignment === "column" &&
            rowNumber === gamesSizesHelper[parentLayout].row)))
    );
  };

  const getTranslationValue = (key, value) => {
    const offsetValue = width < 600 ? TILE_SIZE_RESPONSIVE / 2 : TILE_SIZE / 2;
    if (!value) return 0;
    if (key === "x") {
      if (value === "right") return offsetValue;
      else return offsetValue * -1;
    } else {
      if (value === "top") return offsetValue * -1;
      else return offsetValue;
    }
  };

  const updateGrids = (mode, way, startingLineOrColumn = false) => {
    const keysToMap =
      parentedLayoutsList[parentLayout]?.length > 0
        ? [...parentedLayoutsList[parentLayout], parentLayout]
        : [parentLayout];
    keysToMap.forEach((layoutName) => {
      dispatch(
        updateGridSize({
          mode,
          way,
          parentLayout: layoutName,
          startingLineOrColumn,
        })
      );
    });
  };

  return (
    <ConfiguratorRoot>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isBackdropOn}
      >
        <Paper
          elevation={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "1rem",
          }}
        >
          <CircularProgress color="inherit" sx={{ marginBottom: "0.5rem" }} />
          <Typography variant="body1">
            {t("myExpariences.preparingLogo")}
          </Typography>
        </Paper>
      </Backdrop>
      <ConfigTopOptions
        exparience={props.exparience}
        setExparience={props.setExparience}
      />
      <ConfiguratorLayouts />
      <ObjectsOverview />
      <ConfiguratorModalManager />
      <ConfirmationDialog
        open={confirmation.open}
        element={confirmation.element}
        onAgree={confirmation.agree}
        setOpen={confirmation.setOpen}
        mode="configurator"
      />
      {is3dView ? (
        <>
          <ThreeDimensionsConfigurator is3dView={is3dView} />
          <TileDialog currentTile={currentTile} configObjects={grid} />
        </>
      ) : (
        <>
          <div className={classes.sizingContainer}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box>
                <div className={classes.gridSizingBox}>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => updateGrids("delete", "horizontal", true)}
                    disabled={
                      rowNumber <= 1 ||
                      isReduceLocked("column") ||
                      gamesTypes.includes(parentLayout)
                        ? true
                        : false || isMultiactive
                    }
                  >
                    <Remove />
                  </IconButton>
                  <Typography variant="body1" align="center">
                    {rowNumber}
                  </Typography>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => updateGrids("add", "horizontal", true)}
                    disabled={
                      isMultiactive ||
                      isChildAttachedToLayout() ||
                      gamesTypes.includes(parentLayout)
                    }
                  >
                    <Add />
                  </IconButton>
                </div>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  margin: "0.5rem 0",
                }}
              >
                <div className={classes.gridSizingBox}>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => updateGrids("delete", "vertical", true)}
                    disabled={
                      columnNumber <= 1 ||
                      isReduceLocked("row") ||
                      gamesTypes.includes(parentLayout)
                        ? true
                        : false || isMultiactive
                    }
                  >
                    <Remove />
                  </IconButton>
                  <Typography variant="body1" align="center">
                    {columnNumber}
                  </Typography>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => updateGrids("add", "vertical", true)}
                    disabled={
                      isMultiactive ||
                      isChildAttachedToLayout() ||
                      gamesTypes.includes(parentLayout)
                    }
                  >
                    <Add />
                  </IconButton>
                </div>
                <Typography
                  variant="body1"
                  align="center"
                  className={classes.marginSeparator}
                >
                  x
                </Typography>
                <div className={classes.gridSizingBox}>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => updateGrids("delete", "vertical")}
                    disabled={
                      columnNumber <= 1 || isReduceLocked("row")
                        ? true
                        : false || isMultiactive
                    }
                  >
                    <Remove />
                  </IconButton>
                  <Typography variant="body1" align="center">
                    {columnNumber}
                  </Typography>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => updateGrids("add", "vertical")}
                    disabled={isMultiactive || isChildAttachedToLayout()}
                  >
                    <Add />
                  </IconButton>
                </div>
              </Box>
              <Box>
                <div className={classes.gridSizingBox}>
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={() => updateGrids("delete", "horizontal")}
                    disabled={
                      rowNumber <= 1 || isReduceLocked("column")
                        ? true
                        : false || isMultiactive
                    }
                  >
                    <Remove />
                  </IconButton>
                  <Typography variant="body1" align="center">
                    {rowNumber}
                  </Typography>
                  <IconButton
                    onClick={() => updateGrids("add", "horizontal")}
                    color="primary"
                    size="small"
                    disabled={isMultiactive || isChildAttachedToLayout()}
                  >
                    <Add />
                  </IconButton>
                </div>
              </Box>
            </Box>
            {width <= 975 ? (
              <IconButton
                color="primary"
                size="small"
                className={classes.launchButton}
                onClick={async () => {
                  await props.handleSaveExparience();
                  window.open(props.exparience.arLink, "_blank");
                }}
              >
                <PlayCircle />
              </IconButton>
            ) : (
              <Button
                startIcon={<PlayArrow />}
                variant="contained"
                className={classes.launchButton}
                onClick={async () => {
                  await props.handleSaveExparience();
                  window.open(props.exparience.arLink, "_blank");
                }}
              >
                {t("configurator.launch")}
              </Button>
            )}
          </div>
          <div
            className={`${classes.gridSizing} ${
              currentTile || multiUpload.active || layout === "walls"
                ? classes.gridMargin
                : ""
            }`}
          >
            {gridsKeysToDisplay.map((myGridKey, index) => {
              return (
                <Box
                  key={`grid-${myGridKey}`}
                  sx={{
                    overflow: "hidden",
                    transform: isIsoMode
                      ? "rotateX(60deg) rotateY(0deg) rotateZ(-45deg)"
                      : "inherit",
                    margin:
                      isIsoMode && width >= 1024
                        ? "0 6rem 0 15rem"
                        : isIsoMode && width < 1024
                        ? "0 6rem"
                        : "0 auto",
                    width:
                      width < 600
                        ? `calc(${TILE_SIZE_RESPONSIVE}px * ${
                            columnNumber - 0.25
                          } + 0.8rem)`
                        : `calc(${TILE_SIZE}px * ${
                            columnNumber - 0.25
                          } + 1.6rem)`,
                    height:
                      width < 600
                        ? `calc(${TILE_SIZE_RESPONSIVE}px * ${
                            rowNumber - 0.25
                          } + 0.8rem)`
                        : `calc(${TILE_SIZE}px * ${rowNumber - 0.25} + 1.6rem)`,
                    position: "relative",
                    top:
                      gridsKeysToDisplay.length > 1 && index > 0
                        ? width < 600
                          ? `calc(${TILE_SIZE_RESPONSIVE}px * -${
                              rowNumber - 0.25
                            } - 0.8rem)`
                          : `calc(${TILE_SIZE}px * -${
                              rowNumber - 0.25
                            } - 1.6rem)`
                        : 0,
                    opacity: index % 2 === 0 ? 1 : 0.2,
                  }}
                >
                  {grid[myGridKey]?.map((row, index) => (
                    <div className={classes.rowStyle} key={`row-${index + 1}`}>
                      {row.map((tile, column) => {
                        return (
                          <div
                            key={`tile-${index + 1}-${column + 1}`}
                            style={{ position: "relative" }}
                          >
                            <Tile tile={tile} />
                            {(myGridKey === "basket" ||
                              myGridKey === "football" ||
                              myGridKey === "pickleball") &&
                              tile.row === (rowNumber - 1) / 2 &&
                              tile.column === (columnNumber - 1) / 2 && (
                                <div
                                  className={classes.groundLogoContainer}
                                  style={{
                                    transform:
                                      myGridKey === "pickleball"
                                        ? `scaleX(15.25) scaleY(13) rotate(90deg)`
                                        : `scaleX(17.57) scaleY(18.15) rotate(90deg)`,
                                    opacity:
                                      layout !== "normal" ? "0.2" : "0.5",
                                  }}
                                >
                                  <img
                                    src={
                                      myGridKey === "basket"
                                        ? BasketCourt
                                        : myGridKey === "pickleball"
                                        ? PickleballCourt
                                        : FootballCourt
                                    }
                                    className={classes.imgStyle}
                                    alt={
                                      myGridKey === "basket"
                                        ? "basket court"
                                        : myGridKey === "pickleball"
                                        ? "pickleball court"
                                        : "football court"
                                    }
                                  />
                                </div>
                              )}
                            {/* display ground  */}
                            <div
                              className={classes.groundLogoContainer}
                              style={{
                                transform: ` translateX(${getTranslationValue(
                                  "x",
                                  tile.ground.offset
                                )}px) translateY(${getTranslationValue(
                                  "y",
                                  tile.ground.offsetY
                                )}px) scaleX(${tile.ground.scale}) scaleY(${
                                  tile.ground.scaleY || tile.ground.scale
                                }) rotate(${tile.ground.rotation}deg)`,
                                opacity: layout !== "normal" ? "0.2" : "0.5",
                              }}
                            >
                              {(tile.ground?.file?.name ||
                                tile.ground?.imageUrl?.length > 0) && (
                                <img
                                  src={
                                    tile.ground?.file
                                      ? tile.ground.previewUrl
                                      : tile.ground.imageUrl
                                  }
                                  className={classes.imgStyle}
                                  alt="ground logo"
                                  onLoad={() => {
                                    if (!tile.ground.isLoaded) {
                                      dispatch(
                                        updateElement({
                                          layer: "ground",
                                          data: {
                                            ...tile.ground,
                                            isLoaded: true,
                                          },
                                          tile: {
                                            row: tile.row,
                                            column: tile.column,
                                          },
                                          noSaveTrigger: true,
                                        })
                                      );
                                    }
                                  }}
                                />
                              )}
                              {(tile.ground?.file?.name ||
                                tile.ground?.imageUrl?.length > 0) &&
                                !tile.ground.hasOwnProperty("isLoaded") && (
                                  <Skeleton
                                    variant="rect"
                                    className={classes.skeletonRoot}
                                  />
                                )}
                            </div>
                            {/* display theme */}
                            <div
                              className={classes.themeRadiusWrapper}
                              style={{
                                transform:
                                  tile.theme.areaMode === "radius"
                                    ? `scale(${tile.theme?.radius})`
                                    : `translateX(${getTranslationValue(
                                        "x",
                                        tile.theme.offsetX
                                      )}px) translateY(${getTranslationValue(
                                        "y",
                                        tile.theme.offsetY
                                      )}px) scaleX(${
                                        tile.theme?.scaleX
                                      }) scaleY(${tile.theme?.scaleY})`,
                              }}
                            >
                              {tile.theme?.radius > 0 && (
                                <div
                                  className={classes.themeRadius}
                                  style={{
                                    opacity: layout === "theme" ? "0.6" : "0.4",
                                    borderRadius:
                                      tile.theme.areaMode === "radius"
                                        ? "50%"
                                        : "0",
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </Box>
              );
            })}
          </div>
        </>
      )}
      <TileDialog
        currentTile={currentTile}
        configObjects={props.configObjects}
      />
    </ConfiguratorRoot>
  );
}
